import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { getSeriesDetail } from '@/features/blog/services/blogApi';
import type { Post, Series } from '@/shared/types/api';
import Skeleton from '@/shared/components/Skeleton';
import Breadcrumbs from '@/shared/components/Breadcrumbs';

export default function SeriesPage() {
  const { id } = useParams<{ id: string }>();
  const seriesId = Number(id);

  const { data, isLoading, error } = useQuery<{ series: Series; posts: Post[] }>({
    queryKey: ['series', seriesId],
    queryFn: () => getSeriesDetail(seriesId),
    enabled: !!seriesId,
  });

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-8 w-1/2 mb-4" />
        <Skeleton className="h-4 w-3/4 mb-6" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-full mb-2" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <>
        <Helmet><title>Series Not Found — Vladyslav Chebotarov</title></Helmet>
        <p className="text-muted text-sm">Series not found.</p>
      </>
    );
  }

  const { series, posts } = data;

  return (
    <div>
      <Helmet>
        <title>{series.name} — Series — Vladyslav Chebotarov</title>
        <meta name="description" content={series.description || `${series.name} — a post series`} />
      </Helmet>

      <Breadcrumbs items={[{ label: 'Blog', to: '/blog' }, { label: series.name }]} />

      <h1 className="text-xl font-bold mb-2 text-accent">{series.name}</h1>
      {series.description && (
        <p className="text-sm text-muted mb-6">{series.description}</p>
      )}
      <p className="text-xs text-muted mb-6">{posts.length} post{posts.length !== 1 ? 's' : ''} in this series</p>

      <hr />

      <ol className="list-none p-0 m-0 mt-6 space-y-4">
        {posts.map((post, i) => (
          <li key={post.id} className="flex gap-3 items-baseline">
            <span className="text-muted text-sm font-mono w-6 text-right flex-shrink-0">{post.series_order ?? i + 1}.</span>
            <div>
              <Link to={`/blog/${post.id}/${post.slug}`} className="text-accent hover:underline font-bold text-sm">
                {post.title}
              </Link>
              <div className="text-xs text-muted mt-0.5">
                {format(new Date(post.published_at ?? post.created_at), 'yyyy.MM.dd')}
                {post.excerpt && <span> — {post.excerpt.slice(0, 100)}{post.excerpt.length > 100 ? '...' : ''}</span>}
              </div>
            </div>
          </li>
        ))}
      </ol>

      {posts.length === 0 && (
        <p className="text-muted text-sm mt-6">No posts in this series yet.</p>
      )}
    </div>
  );
}
